import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { toSlug, find, isWorkspaceAdmin } from '../../utils/helpers';

import { openModal } from '../../redux/Slices/modalSlice';
import { config } from '../../config';

import CreateWorkspace from './CreateWorkspace';

import { fetchWorkspaces, updateWorkspace } from '../../redux/Slices/workspaceSlice';
import {
    fetchBoards,
    clearBoardsCache,
    optimisticUpdateBoardInLists,
    optimisticAddBoardToLists,
    optimisticRemoveBoardFromLists
} from '../../redux/Slices/boardsSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';

import WorkspaceSidebar from './WorkspaceSidebar';

import './scss/workspace.scss';
import './scss/Boards.scss';

import BoardSection from './BoardSection';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

const Boards = () => {
    const { workspaces } = useSelector(state => state.workspaces);
    const { shortId } = useParams();
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);

    // Find the current workspace based on the shortId
    const workspace = find.get(workspaces, { name: 'workspace', workspace: shortId });

    // Workspace editing state
    const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
    const [workspaceName, setWorkspaceName] = useState('');
    const [workspaceUpdateStatus, setWorkspaceUpdateStatus] = useState('');

    // Get workspace and check if current user is admin
    const currentUserId = user?.user?._id;
    const currentUserIsAdmin = isWorkspaceAdmin(workspace, currentUserId);


    // Dispatch fetchWorkspaces on component mount
    useEffect(() => {
        dispatch(fetchWorkspaces());
    }, [dispatch]);

    // Fetch boards for the specific workspace when it changes
    useEffect(() => {
        if (shortId) {
            dispatch(clearBoardsCache());
            dispatch(fetchBoards({ workspaceId: shortId, useCache: false }));
        }
    }, [dispatch, shortId]);

    // Update workspace name when workspace changes
    useEffect(() => {
        if (workspace) {
            setWorkspaceName(workspace.name);
        }
    }, [workspace]);

    // Real-time socket listener for board changes
    useSocketAction([
        ActionTypes.BOARD_CREATED,
        ActionTypes.BOARD_UPDATED,
        ActionTypes.BOARD_DELETED,
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
    ], (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        console.log('[Boards] Real-time board event:', data.action, data);

        switch (data.action) {
            case ActionTypes.BOARD_CREATED:
                // ✅ Add board to boards list using optimistic action
                if (data.board) {
                    dispatch(optimisticAddBoardToLists(data.board));
                }
                break;

            case ActionTypes.BOARD_UPDATED:
                // ✅ Update board in boards list using optimistic action
                if (data.boardId && (data.board || data.updatedFields)) {
                    const updates = data.board || data.updatedFields;
                    dispatch(optimisticUpdateBoardInLists({
                        boardId: data.boardId,
                        updates
                    }));
                }
                break;

            case ActionTypes.BOARD_DELETED:
                // ✅ Remove board from boards list using optimistic action
                if (data.boardId) {
                    dispatch(optimisticRemoveBoardFromLists(data.boardId));
                }
                break;

            case ActionTypes.MEMBER_ADDED:
                // ✅ Board member added - if current user was added, show the board
                if (data.boardId && data.board) {
                    const isCurrentUserAdded = data.newMembers?.some(
                        nm => nm.user.toString() === user?.user?._id
                    );

                    if (isCurrentUserAdded) {
                        // Add the board to the user's board lists
                        dispatch(optimisticAddBoardToLists(data.board));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            case ActionTypes.MEMBER_REMOVED:
                // ✅ Board member removed - if current user was removed, hide the board
                if (data.boardId && data.memberId) {
                    if (data.memberId === user?.user?._id) {
                        // Remove the board from the user's board lists
                        dispatch(optimisticRemoveBoardFromLists(data.boardId));
                    } else {
                        // Update board members in existing board
                        dispatch(optimisticUpdateBoardInLists({
                            boardId: data.boardId,
                            updates: { members: data.members }
                        }));
                    }
                }
                break;

            default:
                break;
        }
    }, [dispatch, user?.user?._id]);

    const handleCreateWorkspace = () => {
        dispatch(openModal({ modalType: 'CREATE_WORKSPACE', modalData: {} }));
    };

    const [openWorkspaces, setOpenWorkspaces] = useState({});

    useEffect(() => {
        const initialOpenWorkspaces = {};
        workspaces.slice(0, 2).forEach(workspace => {
            initialOpenWorkspaces[workspace.name] = true;
        });
        setOpenWorkspaces(initialOpenWorkspaces);
    }, [workspaces]);

    const toggleOptions = (workspaceName) => {
        setOpenWorkspaces(prevState => ({
            ...prevState,
            [workspaceName]: !prevState[workspaceName]
        }));
    };

    // Workspace name update handlers
    const handleEditWorkspaceName = () => {
        if (workspace) {
            setIsEditingWorkspaceName(true);
        }
    };

    const handleUpdateWorkspaceName = async () => {
        if (!workspace || !workspaceName.trim() || workspaceName.trim() === workspace.name) {
            setIsEditingWorkspaceName(false);
            setWorkspaceName(workspace?.name || '');
            return;
        }

        try {
            setWorkspaceUpdateStatus('saving');
            await dispatch(updateWorkspace({
                workspaceId: workspace.shortId,
                name: workspaceName.trim()
            })).unwrap();

            setWorkspaceUpdateStatus('saved');
            setIsEditingWorkspaceName(false);
            dispatch(showSnackBar({ message: 'Workspace name updated successfully', type: 'success' }));

            // Clear saved status after 2 seconds
            setTimeout(() => setWorkspaceUpdateStatus(''), 2000);
        } catch (error) {
            console.error('Error updating workspace name:', error);
            setWorkspaceUpdateStatus('error');
            dispatch(showSnackBar({ message: 'Failed to update workspace name', type: 'error' }));

            // Reset to original name on error
            setWorkspaceName(workspace?.name || '');
            setIsEditingWorkspaceName(false);

            // Clear error status after 3 seconds
            setTimeout(() => setWorkspaceUpdateStatus(''), 3000);
        }
    };

    const handleWorkspaceNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleUpdateWorkspaceName();
        } else if (e.key === 'Escape') {
            setIsEditingWorkspaceName(false);
            setWorkspaceName(workspace?.name || '');
        }
    };

    return (
        <div className="zppbbe-workspace-wrapper">
            <div className="zoobbe-workspace-container">
                <div className="zoobbe-sidebar-container">

                    <WorkspaceSidebar
                        openWorkspaces={openWorkspaces}
                        toggleOptions={toggleOptions}
                        handleCreateWorkspace={handleCreateWorkspace}
                        setOpenWorkspaces={setOpenWorkspaces}
                    />
                </div>

                <div className='zoobbe-main-content-container'>
                    <div className="zoobbe-main-content">
                        {workspace ? (
                            <>
                                <div className="board-header">
                                    <div className="board-icon">
                                        <span className="icon-text">Z</span>
                                    </div>
                                    <div className="board-details">
                                        <div className="board-name">
                                            {isEditingWorkspaceName ? (
                                                <input
                                                    type="text"
                                                    value={workspaceName}
                                                    onChange={(e) => setWorkspaceName(e.target.value)}
                                                    onBlur={handleUpdateWorkspaceName}
                                                    onKeyDown={handleWorkspaceNameKeyDown}
                                                    className="workspace-name-input"
                                                    autoFocus
                                                    maxLength={50}
                                                />
                                            ) : (

                                                <>
                                                    <span>{workspaceName}</span>
                                                    {currentUserIsAdmin && (
                                                        <span
                                                            className="material-symbols-outlined edit-icon"
                                                            onClick={handleEditWorkspaceName}
                                                            title="Edit workspace name"
                                                        >
                                                            edit
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                            {workspaceUpdateStatus && (
                                                <div className="workspace-update-status">
                                                    {workspaceUpdateStatus === 'saving' && (
                                                        <span className="save-status saving">
                                                            <span className="material-symbols-outlined">sync</span>
                                                        </span>
                                                    )}
                                                    {workspaceUpdateStatus === 'saved' && (
                                                        <span className="save-status saved">
                                                            <CircleCheckOutlined />
                                                        </span>
                                                    )}
                                                    {workspaceUpdateStatus === 'error' && (
                                                        <span className="save-status error">
                                                            <span className="material-symbols-outlined">error</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="board-visibility">
                                            <span className="material-symbols-outlined">
                                                lock
                                            </span>
                                            <span>Private</span>
                                        </div>
                                    </div>
                                </div>

                                <div className='boards-content-header'>
                                    <span className='material-symbols-outlined'>
                                        person
                                    </span>
                                    <h4 className='header-title'>
                                        Your boards
                                    </h4>
                                </div>

                                <BoardSection workspace={workspace} defaultSelectedOption={0} />
                            </>
                        ) : (
                            <p>Loading workspace...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Boards;
